// Compatibility bridge: existing page variables keep working, while a partner
// theme controls semantic roles. No rules are applied for the Valovate baseline.
import {desktopThemeAlphas} from './desktopThemeBindings';
const v = name => `var(--brand-${name})`;
const mix = (a, amount, b) => `color-mix(in srgb, ${a} ${amount}%, ${b})`;
const white = 'var(--palette-ffffff)';
const black = 'var(--palette-000000)';
// These existing controls pair accent backgrounds with white labels. Give
// them an action-specific role instead of pale heading accents in the preview.
const actionSelectors = '.calendar-workspace .calendar-create-event, .MyHomework button.MyHomework__primary, .Protocols button.Protocols__primary, .MatchPrep button.MatchPrep__primary, .TeamVoting nav button[aria-pressed="true"], .TeamVoting button[type="submit"], .PrepActions--save > button:first-of-type, .ReviewSetupEditor__actions button:first-child, .gdd1-chip--link';

const surfaceFamilies = {
  page: '02070d 03080f 04070f 04080e 04131c 050c14 06121a 061521 070c12 070e16 071019 080e18 08101b 08121b 090f18 09111a 09111b 0a1018 0a121b 0a141e 0a1722 0b121d 0b1220 0b1925 0b1d2b 0c121b 0c1926 0d1524 0e1722 0f1320 0f172a 0f1925 101720 101827 101925 101b28',
  card: '111827 111a28 111b28 112737 121826 121c28 121d30 141414 171e29 181822 181b20 18222f 19212d 1a1d24 1c2430 1e1e2e 1e293b',
  elevated: '202938 232f40 253246 273449 283447 2a2d3a 2c3e50 334155 374151',
};

export function buildBrandTokens(theme, mode = 'dark') {
  if (!theme?.colors || !['accent', 'pale', 'primary', 'secondary'].every(key => /^#[\da-f]{6}$/i.test(theme.colors[key]))) {
    throw new Error('A brand needs four six-digit hexadecimal colors: accent, pale, primary, secondary.');
  }
  const light = mode === 'light';
  const tokens = Object.fromEntries(Object.entries(theme.colors).map(([key, value]) => [`--brand-${key}`, value]));
  Object.assign(tokens, {
    '--brand-page-dark': mix(v('primary'), 8, mix(white, 5, black)),
    '--brand-card-dark': mix(v('primary'), 10, mix(white, 14, black)),
    '--brand-elevated-dark': mix(v('primary'), 13, mix(white, 18, black)),
    '--brand-page-light': mix(v('pale'), 12, white),
    '--brand-card-light': mix(v('pale'), 28, white),
    '--brand-ink': mix(v('primary'), 10, black),
    '--brand-text': mix(v('pale'), 8, white),
    '--brand-heading': v(light ? 'secondary' : 'accent'),
    '--brand-action': v('primary'),
    '--brand-action-hover': v('secondary'),
    '--brand-action-text': white,
    '--brand-border': mix(v('accent'), light ? 65 : 32, v(light ? 'page-light' : 'page-dark')),
    '--brand-muted': mix(v(light ? 'ink' : 'text'), 70, v(light ? 'page-light' : 'page-dark')),
    '--clr159db5': v(light ? 'secondary' : 'accent'),
    '--clr212A37': v('card-dark'),
    '--clr0e131B': v('page-dark'),
    '--clr1d5a6d': v('secondary'),
    '--clrfd4556': v('action'),
    '--clrf7fcfe': v('text'),
    '--clr0d313f': v('ink'),
    '--clrd3f7fa': v('pale'),
    '--clr22bbd7': v('accent'),
  });
  if (theme.surfaces) Object.assign(tokens, {
    '--brand-page-dark':theme.surfaces.darkPage,
    '--brand-card-dark':theme.surfaces.darkCard,
    '--brand-elevated-dark':theme.surfaces.darkElevated,
    '--brand-page-light':theme.surfaces.lightPage,
    '--brand-card-light':theme.surfaces.lightCard,
  });
  Object.assign(tokens, {
    '--desktop-accent':v('heading'), '--desktop-card':v('card-dark'), '--desktop-bg':v('page-dark'),
    '--desktop-heading':v('heading'), '--desktop-alert':v('action'), '--desktop-text':v('text'),
    '--desktop-cta-hover':v('ink'), '--desktop-sidebar':v('page-dark'), '--desktop-border':v('border'),
    '--desktop-border-strong':v('border'), '--desktop-muted':v('muted'),
  });
  for (const role of ['heading','accent','secondary','ink','card-dark','page-dark','elevated-dark','text','pale']) {
    tokens[`--team-ui-${role}`]=v(role);
    for (const alpha of desktopThemeAlphas[role] || []) tokens[`--team-ui-${role}-a${String(alpha).replace('.','p')}`]=mix(v(role),alpha*100,'transparent');
  }
  // Explicitly selected structural shades, not a hue-based recoloring of every
  // color: status, role, event-tag and distinct chart colors remain untouched.
  for (const [family, colors] of Object.entries(surfaceFamilies)) {
    for (const color of colors.split(' ')) tokens[`--palette-${color}`] = v(`${family}-dark`);
  }
  for (const color of ['00bdd6', '00bed1', '00c9df', '087489', '08778a', '087f96', '116781']) {
    tokens[`--palette-${color}`] = v('heading');
  }
  for (const color of ['e8f4fc', 'f4f8fc', 'f5f8fe', 'fbfeff', 'eaf4ff']) tokens[`--palette-${color}`] = v('page-light');
  for (const color of ['cce7ff', 'd9ebfb', 'e1e9f2', 'dce8f2']) tokens[`--palette-${color}`] = v('card-light');
  for (const name of ['dark-bg1', 'dark-bg2', 'general-dark-bg', 'Page-bg-clr-dark', 'Bg-clr-dark']) tokens[`--${name}`] = v('page-dark');
  for (const name of ['dark-bg3', 'dark-bg4', 'dark-purple', 'general-dark-accordion-bg', 'general-dark-accordion-main']) tokens[`--${name}`] = v('card-dark');
  for (const name of ['dark-bg5', 'dark-grey1', 'dark-grey2']) tokens[`--${name}`] = v('elevated-dark');
  for (const name of ['dark-accent1', 'dark-accent2', 'dark-accent3', 'dark-shadow', 'dark-border', 'general-dark-title', 'general-dark-border', 'general-dark-sublabel', 'sidebar-blue-accent', 'sidebar-hover-blue']) tokens[`--${name}`] = v('accent');
  for (const name of ['light-bg1', 'light-bg4', 'general-light-bg']) tokens[`--${name}`] = v('page-light');
  for (const name of ['light-bg2', 'light-bg3', 'light-box-bg']) tokens[`--${name}`] = v('card-light');
  for (const name of ['light-accent1', 'light-accent2', 'light-border', 'general-light-title', 'general-light-border', 'sidebar-deep-blue']) tokens[`--${name}`] = v('secondary');
  tokens['--light-accent3'] = v('pale');
  return tokens;
}

export function brandPreviewAllowed(environment, hostname, enabled = true) {
  return enabled && environment === 'development' && ['localhost', '127.0.0.1', '[::1]', '::1'].includes(hostname);
}

export function applyBrandPreview(root, theme, mode, enabled) {
  const id = 'valovate-brand-preview-palette';
  const existing = root.ownerDocument.getElementById(id);
  if (!enabled) {
    existing?.remove();
    root.removeAttribute('data-brand-preview');
    return;
  }
  const style = existing || root.ownerDocument.createElement('style');
  style.id = id;
  style.textContent = brandThemeCss(theme, mode, ':root[data-brand-preview="partner"]');
  if (!existing) root.ownerDocument.head.appendChild(style);
  root.setAttribute('data-brand-preview', 'partner');
}

export function brandThemeCss(theme, mode, selector) {
  const tokens = buildBrandTokens(theme, mode);
  // Higher specificity than page-level :root definitions; no inline styles or
  // palette values are overwritten, so removing this layer restores everything.
  return `${selector}{${Object.entries(tokens).map(([key, value]) => `${key}:${value};`).join('')}}
    ${selector} :is(${actionSelectors}){background:var(--brand-action);color:var(--brand-action-text);border-color:var(--brand-action);}
    ${selector} :is(${actionSelectors}):hover{background:var(--brand-action-hover);color:var(--brand-action-text);border-color:var(--brand-action-hover);}
    ${selector} .sidebar-svg-image{filter:none!important;background-color:var(--nsb-icon,var(--brand-heading));mask-image:var(--sidebar-svg-source);mask-size:contain;mask-position:center;mask-repeat:no-repeat;-webkit-mask-image:var(--sidebar-svg-source);-webkit-mask-size:contain;-webkit-mask-position:center;-webkit-mask-repeat:no-repeat;}
    ${selector} .sidebar-svg-image > img{visibility:hidden;}`;
}
